/**
 * El asistente de aumento masivo.
 *
 * Es la operación más destructiva de la consola —toca TODOS los precios
 * vigentes de una— así que lo que se pueda sacar de la pantalla y testear, se
 * saca. Estas tres funciones son las que deciden qué se le manda al worker y
 * cuándo se lo deja aplicar.
 */

/** Lo que se tipea en el paso de configuración, todo como texto. */
export interface ConfiguracionDelAumento {
  porcentajeNormal: string;
  porcentajeVip: string;
  /** El default: el precio VIP sube lo mismo que el normal. */
  vipAtadoAlNormal: boolean;
  vigenteDesde: string;
  vigenteHasta: string;
  /** Vacío = todo el catálogo. */
  productoId: string;
}

/**
 * El cuerpo del `POST /precios/aumento`.
 *
 * **Los campos vacíos se OMITEN, no se mandan en blanco.** El endpoint
 * distingue: sin `vigenteHasta` la tarifa nueva no vence, y un `productoId`
 * vacío lo haría buscar combinaciones de un producto inexistente y contestar
 * 422 sobre un aumento que era para todo el catálogo.
 *
 * `vigenteHasta` y `productoId` los acepta el worker **desde siempre** y
 * ninguna pantalla los mandaba (ADR 0070).
 */
export function cuerpoDelAumento(config: ConfiguracionDelAumento): Record<string, unknown> {
  const normal = Number(config.porcentajeNormal);
  const vipTipeado = Number(config.porcentajeVip);
  // Atado, o desatado pero en blanco: el VIP sigue al normal. Mandar `NaN`
  // haría que el worker conteste «los porcentajes deben ser numeros» sobre un
  // campo que la persona dejó vacío a propósito.
  const vip =
    config.vipAtadoAlNormal || config.porcentajeVip === "" || !Number.isFinite(vipTipeado)
      ? normal
      : vipTipeado;

  return {
    porcentajeNormal: normal,
    porcentajeVip: vip,
    vigenteDesde: config.vigenteDesde,
    ...(config.vigenteHasta ? { vigenteHasta: config.vigenteHasta } : {}),
    ...(config.productoId ? { productoId: config.productoId } : {}),
  };
}

/**
 * El rango de `imprenta`: de -99,99 % a 999,99 %.
 *
 * El piso no es arbitrario: -100 % deja todos los precios en cero. El worker
 * también lo frena —rechaza el aumento entero si algún precio queda en cero o
 * negativo— y esto solo evita el viaje.
 */
export function porcentajeValido(valor: string): boolean {
  if (valor.trim() === "") return false;
  const n = Number(valor);
  return Number.isFinite(n) && n > -100 && n <= 999.99;
}

/**
 * La frase que hay que escribir para aplicar, como el `ConfirmModal` de
 * `imprenta`.
 *
 * Escribirla en mayúsculas es el freno: es el momento en que uno se pregunta si
 * de verdad quiere tocar todos los precios. Por eso no se acepta en minúscula.
 */
export const FRASE_DE_CONFIRMACION = "APLICAR CAMBIOS";

export function confirmacionEsValida(texto: string): boolean {
  return texto.trim() === FRASE_DE_CONFIRMACION;
}
